// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { useFormik } from "formik";
// COMPONENTS
import { Button, Card, Form, Row, Col, Table, Alert, Spinner } from "react-bootstrap";
import MUIDataTable from "mui-datatables";
// CUSTOM COMPONENTS
import * as alert from "../../../redux/alertSnackBarRedux";
// SERVICES
import * as yearTransitionService from "../../../services/service/yearTransitionService";

const YearTransitionPage = (props) => {
    const { showAlert } = props;
    const [loading, setLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [validation, setValidation] = useState(null);
    const [existingProposals, setExistingProposals] = useState([]);
    const [config, setConfig] = useState(null);
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear + 1);

    useEffect(() => {
        loadConfig();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            validateYear(selectedYear);
            loadExistingProposals(selectedYear);
        }
    }, [selectedYear]);

    const loadConfig = () => {
        setConfigLoading(true);
        yearTransitionService.GetYearTransitionConfig()
            .then(res => {
                const data = res.data || res;
                setConfig(data);
                formik.setFieldValue('contactName', data.contactName);
                formik.setFieldValue('contactPhone', data.contactPhone);
                formik.setFieldValue('contactEmail', data.contactEmail);
                formik.setFieldValue('additionalEmails', data.additionalEmails);
                setConfigLoading(false);
            })
            .catch(ex => {
                showAlert({ message: ex.message || "Failed to load configuration", variant: "error" });
                setConfigLoading(false);
            });
    };

    const validateYear = (year) => {
        yearTransitionService.ValidateYearTransition(year)
            .then(res => {
                console.log('Validation response:', res);
                // Axios interceptor might unwrap the response, check both
                const data = res.data || res;
                console.log('Setting validation:', data);
                setValidation(data);
            })
            .catch(ex => {
                console.error('Validation error:', ex);
                showAlert({ message: ex.message || "Failed to validate year", variant: "error" });
            });
    };

    const loadExistingProposals = (year) => {
        yearTransitionService.GetAdminProposalsByYear(year)
            .then(res => {
                const data = res.data || res;
                setExistingProposals(data);
            })
            .catch(ex => {
                console.error("Failed to load existing proposals:", ex);
            });
    };

    const formik = useFormik({
        initialValues: {
            contactName: "",
            contactPhone: "",
            contactEmail: "",
            additionalEmails: "",
            estimatedTotalCost: ""
        },
        onSubmit: (values) => {
            if (!validation || !validation.isValid) {
                showAlert({ message: "Please fix validation errors before creating proposals", variant: "error" });
                return;
            }

            const yearPrefix = selectedYear.toString().substring(2);
            const adminProposals = [
                {
                    projectNumber: `${yearPrefix}-0001-01`,
                    projectSubTypeId: 65,
                    description: "Administrative",
                    projectName: "Administrative",
                    estimatedTotalCost: values.estimatedTotalCost ? parseFloat(values.estimatedTotalCost) : null,
                    proposalSenderSignatureUserId: 3,
                    proposalSenderName: "Chip Pierrotti",
                    proposalSenderTitle: "President"
                },
                {
                    projectNumber: `${yearPrefix}-0002-01`,
                    projectSubTypeId: 66,
                    description: "Paid Time Off (PTO)",
                    projectName: "Administrative",
                    estimatedTotalCost: null,
                    proposalSenderSignatureUserId: 5,
                    proposalSenderName: null,
                    proposalSenderTitle: null
                },
                {
                    projectNumber: `${yearPrefix}-0003-01`,
                    projectSubTypeId: 67,
                    description: "Company Holiday (paid)",
                    projectName: "Administrative",
                    estimatedTotalCost: null,
                    proposalSenderSignatureUserId: 5,
                    proposalSenderName: null,
                    proposalSenderTitle: null
                },
                {
                    projectNumber: `${yearPrefix}-0004-01`,
                    projectSubTypeId: 66,
                    description: "Non Paid Time Off (NPTO)",
                    projectName: "Administrative",
                    estimatedTotalCost: null,
                    proposalSenderSignatureUserId: 5,
                    proposalSenderName: null,
                    proposalSenderTitle: null
                }
            ];

            const model = {
                targetYear: selectedYear,
                contactName: values.contactName,
                contactPhone: values.contactPhone,
                contactEmail: values.contactEmail,
                additionalEmails: values.additionalEmails,
                adminProposals: adminProposals
            };

            setLoading(true);
            yearTransitionService.CreateAdminProposals(model)
                .then(res => {
                    const data = res.data || res;
                    if (data && data.success) {
                        showAlert({ message: data.message, variant: "success" });
                        loadExistingProposals(selectedYear);
                        validateYear(selectedYear);
                    } else {
                        showAlert({ message: data?.message || "Failed to create proposals", variant: "error" });
                    }
                    setLoading(false);
                })
                .catch(ex => {
                    showAlert({ message: ex.response?.data?.message || ex.message || "Failed to create proposals", variant: "error" });
                    setLoading(false);
                });
        }
    });

    const yearOptions = [];
    for (let i = 0; i <= 10; i++) {
        yearOptions.push(currentYear + i);
    }

    const columns = [
        {
            name: "projectNumber",
            label: "Project Number",
            options: { filter: false, sort: true }
        },
        {
            name: "description",
            label: "Description",
            options: { filter: false, sort: false }
        },
        {
            name: "projectSubType",
            label: "Project SubType",
            options: { filter: true, sort: true }
        },
        {
            name: "estimatedTotalCost",
            label: "Estimated Cost",
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => value ? `$${value.toFixed(2)}` : "N/A"
            }
        },
        {
            name: "approvedOrRejectedDateTime",
            label: "Approved Date",
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value) => value ? new Date(value).toLocaleDateString() : "N/A"
            }
        },
        {
            name: "hasApproved",
            label: "Status",
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => value ?
                    <span className="label label-lg label-success label-inline">Approved</span> :
                    <span className="label label-lg label-warning label-inline">Pending</span>
            }
        }
    ];

    return (
        <div className="card card-custom">
            <div className="card-header">
                <h3 className="card-title">Year Transition Management</h3>
            </div>
            <div className="card-body">
                {configLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading configuration...</p>
                    </div>
                ) : (
                    <>
                        {/* Year Selection */}
                        <Card className="mb-5">
                            <Card.Body>
                                <h5 className="mb-4">Select Target Year</h5>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Target Year *</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={selectedYear}
                                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            >
                                                {yearOptions.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>&nbsp;</Form.Label>
                                            <div className="alert alert-info mb-0">
                                                <strong>Current Year:</strong> {currentYear}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Validation Messages */}
                        {validation && (
                            <Card className="mb-5">
                                <Card.Body>
                                    {validation.errors && validation.errors.length > 0 && (
                                        <Alert variant="danger">
                                            <strong>Errors:</strong>
                                            <ul className="mb-0 mt-2">
                                                {validation.errors.map((error, idx) => (
                                                    <li key={idx}>{error}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                    {validation.warnings && validation.warnings.length > 0 && (
                                        <Alert variant="warning">
                                            <strong>Warnings:</strong>
                                            <ul className="mb-0 mt-2">
                                                {validation.warnings.map((warning, idx) => (
                                                    <li key={idx}>{warning}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                    {validation.isValid && !validation.adminProposalsExist && (
                                        <Alert variant="success">
                                            Year {selectedYear} is ready for admin proposal creation.
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Configuration Form */}
                        {console.log('Render check - validation:', validation, 'isValid:', validation?.isValid, 'adminProposalsExist:', validation?.adminProposalsExist)}
                        {validation && validation.isValid && !validation.adminProposalsExist && (
                            <Card className="mb-5">
                                <Card.Body>
                                    <h5 className="mb-4">Admin Proposal Configuration</h5>
                                    <Form onSubmit={formik.handleSubmit}>
                                        <Row>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Contact Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        {...formik.getFieldProps("contactName")}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Contact Phone *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        {...formik.getFieldProps("contactPhone")}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Contact Email *</Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        {...formik.getFieldProps("contactEmail")}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Additional Emails</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        {...formik.getFieldProps("additionalEmails")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mt-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Estimated Total Cost (for Admin proposal only)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        {...formik.getFieldProps("estimatedTotalCost")}
                                                        placeholder="Optional"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <hr className="my-5" />

                                        <h6 className="mb-3">Proposals to be Created:</h6>
                                        <Table bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Project Number</th>
                                                    <th>Description</th>
                                                    <th>Project SubType</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{selectedYear.toString().substring(2)}-0001-01</td>
                                                    <td>Administrative</td>
                                                    <td>Administrative (General)</td>
                                                </tr>
                                                <tr>
                                                    <td>{selectedYear.toString().substring(2)}-0002-01</td>
                                                    <td>Paid Time Off (PTO)</td>
                                                    <td>PTO/NPTO</td>
                                                </tr>
                                                <tr>
                                                    <td>{selectedYear.toString().substring(2)}-0003-01</td>
                                                    <td>Company Holiday (paid)</td>
                                                    <td>Holiday</td>
                                                </tr>
                                                <tr>
                                                    <td>{selectedYear.toString().substring(2)}-0004-01</td>
                                                    <td>Non Paid Time Off (NPTO)</td>
                                                    <td>PTO/NPTO</td>
                                                </tr>
                                            </tbody>
                                        </Table>

                                        <div className="mt-5">
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                disabled={loading}
                                                className="font-weight-bold px-9 py-4"
                                            >
                                                {loading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="mr-2" />
                                                        Creating Proposals...
                                                    </>
                                                ) : (
                                                    `Create Admin Proposals for ${selectedYear}`
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Existing Proposals Table */}
                        {existingProposals && existingProposals.length > 0 && (
                            <Card>
                                <Card.Body>
                                    <h5 className="mb-4">Existing Admin Proposals for {selectedYear}</h5>
                                    <MUIDataTable
                                        data={existingProposals}
                                        columns={columns}
                                        options={{
                                            filterType: "multiselect",
                                            selectableRows: "none",
                                            rowsPerPageOptions: [10, 25, 50],
                                            download: true,
                                            print: true,
                                            responsive: "standard"
                                        }}
                                    />
                                </Card.Body>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, alert.actions)(YearTransitionPage);
